import { AbstractControl, FormArray, FormGroup, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { FormGroupOf, TimeSpan } from '@shared/models';

import { getNestedField, nameofPath, PropertyName } from './data.utils';
import { FormGroupExtension } from './form-group.utils';

export class FormGroupValidators {
  public static requiredAnyOfValidator<const TControlName extends string>(controlNames: TControlName[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof UntypedFormGroup || control instanceof FormGroup)) {
        return null;
      }

      const hasValue = controlNames.some((controlName) => {
        const controlValue = control.controls[controlName]?.value;

        return Array.isArray(controlValue) ? controlValue.length > 0 : !!controlValue;
      });

      return hasValue ? null : { required: true };
    };
  }

  public static hasDuplicateInFormArrayOfFormGroupValidator(
    translateService: TranslateService,
    primaryFormControlName: string
  ) {
    return (control: AbstractControl): { duplicate: string } | null => {
      if (
        control?.parent?.parent &&
        (control?.parent instanceof UntypedFormGroup || control?.parent instanceof FormGroup) &&
        control?.parent?.parent instanceof FormArray
      ) {
        const controlName = FormGroupExtension.getControlName(control);
        const formArray = control?.parent?.parent as FormArray<UntypedFormGroup>;
        const formArrayIndex = +FormGroupExtension.getControlName(control?.parent);

        const duplicateForm = formArray.controls?.find(
          (x, ix) =>
            ix !== formArrayIndex && x.controls[controlName]?.value?.toLowerCase() === control.value?.toLowerCase()
        );
        if (duplicateForm) {
          return {
            duplicate: translateService.instant('error.itemExists', {
              primaryControl: primaryFormControlName,
              value: control.value,
            }),
          };
        }
      }

      return null;
    };
  }

  public static requiredLoverValueAsValidator(
    translateService: TranslateService,
    primaryFormControlName: string,
    primaryLabelTranslationKey: string | null = null,
    secondaryLabelTranslationKey: string | null = null
  ) {
    return (control: AbstractControl): { duplicate: string } | null => {
      if (control?.parent && (control?.parent instanceof UntypedFormGroup || control?.parent instanceof FormGroup)) {
        if (control?.parent.controls[primaryFormControlName]?.value >= control.value) {
          return {
            duplicate: translateService.instant('error.greaterValue', {
              primaryControl: primaryLabelTranslationKey
                ? translateService.instant(primaryLabelTranslationKey)
                : primaryFormControlName,
              secondaryControl: secondaryLabelTranslationKey
                ? translateService.instant(secondaryLabelTranslationKey)
                : FormGroupExtension.getControlName(control),
            }),
          };
        }
      }
      return null;
    };
  }

  public static requiredNotSameValueAsValidator(
    translateService: TranslateService,
    primaryFormControlName: string,
    primaryLabelTranslationKey: string | null = null,
    secondaryLabelTranslationKey: string | null = null
  ) {
    return (control: AbstractControl): { duplicate: string } | null => {
      if (control?.parent && (control?.parent instanceof UntypedFormGroup || control?.parent instanceof FormGroup)) {
        if (control?.parent.controls[primaryFormControlName]?.value === control.value) {
          return {
            duplicate: translateService.instant('error.sameValue', {
              primaryControl: primaryLabelTranslationKey
                ? translateService.instant(primaryLabelTranslationKey)
                : primaryFormControlName,
              secondaryControl: secondaryLabelTranslationKey
                ? translateService.instant(secondaryLabelTranslationKey)
                : FormGroupExtension.getControlName(control),
            }),
          };
        }
      }
      return null;
    };
  }

  public static requiredSameValueAsValidator(
    translateService: TranslateService,
    primaryFormControlName: string,
    primaryLabelTranslationKey: string | null = null,
    secondaryLabelTranslationKey: string | null = null
  ) {
    return (control: AbstractControl): { duplicate: string } | null => {
      if (control?.parent && (control?.parent instanceof UntypedFormGroup || control?.parent instanceof FormGroup)) {
        if (control?.parent.controls[primaryFormControlName]?.value !== control.value) {
          return {
            duplicate: translateService.instant('error.notSameValue', {
              primaryControl: primaryLabelTranslationKey
                ? translateService.instant(primaryLabelTranslationKey)
                : primaryFormControlName,
              secondaryControl: secondaryLabelTranslationKey
                ? translateService.instant(secondaryLabelTranslationKey)
                : FormGroupExtension.getControlName(control),
            }),
          };
        }
      }
      return null;
    };
  }

  public static conditionalValidator<T>(
    predicate: (siblingValue: T) => boolean,
    validators: ValidatorFn | ValidatorFn[],
    valueChangeControl?: PropertyName<FormGroup<FormGroupOf<T>>>
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = control?.parent as FormGroup<FormGroupOf<T>> | null;
      if (!parent) {
        return null;
      }

      if (valueChangeControl) {
        const path = nameofPath(valueChangeControl);
        const controlWithMeta = control as AbstractControl & Record<string, unknown>;

        if (!controlWithMeta[path]) {
          getNestedField<FormGroup<FormGroupOf<T>>, AbstractControl>(parent, path)?.valueChanges.subscribe(() => {
            control.updateValueAndValidity({ onlySelf: true });
          });
          controlWithMeta[path] = true;
        }
      }

      getNestedField<FormGroup<FormGroupOf<T>>, AbstractControl>(
        parent,
        nameofPath(valueChangeControl)
      )?.valueChanges.subscribe(() => control.updateValueAndValidity({ onlySelf: true }));

      if (!predicate(parent.getRawValue() as T)) {
        return null;
      }

      let errors: ValidationErrors | null = null;

      for (const fn of Array.isArray(validators) ? validators : [validators]) {
        const result = fn(control);
        if (result) {
          errors = { ...(errors ?? {}), ...result };
        }
      }

      return errors;
    };
  }

  public static timeSpanValidator(options: {
    minDays?: number;
    maxDays?: number;
    minHours?: number;
    maxHours?: number;
    minMinutes?: number;
    maxMinutes?: number;
    minSeconds?: number;
    maxSeconds?: number;
  }) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value === null || value === undefined || value.toString().trim() === '') {
        return null;
      }

      const text = value.toString().trim();

      if (!TimeSpan.isValid(text)) {
        return { timeSpanFormat: true };
      }

      const span = new TimeSpan(text);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errors: any = {};

      if (options.minDays !== undefined && span.days < options.minDays) {
        errors.timeSpanMinDays = true;
      }
      if (options.maxDays !== undefined && span.days > options.maxDays) {
        errors.timeSpanMaxDays = true;
      }

      if (options.minHours !== undefined && span.hours < options.minHours) {
        errors.timeSpanMinHours = true;
      }
      if (options.maxHours !== undefined && span.hours > options.maxHours) {
        errors.timeSpanMaxHours = true;
      }

      if (options.minMinutes !== undefined && span.minutes < options.minMinutes) {
        errors.timeSpanMinMinutes = true;
      }
      if (options.maxMinutes !== undefined && span.minutes > options.maxMinutes) {
        errors.timeSpanMaxMinutes = true;
      }

      if (options.minSeconds !== undefined && span.seconds < options.minSeconds) {
        errors.timeSpanMinSeconds = true;
      }
      if (options.maxSeconds !== undefined && span.seconds > options.maxSeconds) {
        errors.timeSpanMaxSeconds = true;
      }

      return Object.keys(errors).length ? errors : null;
    };
  }
}
